import { RealtimeProvider as CoreRealtimeProvider } from '@liniya/client-core';
import type { RealtimeCursorStorage } from '@liniya/client-core';
import type { ReactNode } from 'react';

export { useRealtime } from '@liniya/client-core';

/**
 * Keeps the session's cursor for the lifetime of the tab.
 *
 * `sessionStorage` rather than `localStorage`: the cursor belongs to one tab's
 * view of the event stream, and two tabs sharing one cursor would each skip
 * the events the other consumed.
 */
class SessionCursorStorage implements RealtimeCursorStorage {
  private readonly key = 'liniya.realtime.cursor';

  read(): number {
    try {
      return Number(globalThis.sessionStorage?.getItem(this.key)) || 0;
    } catch {
      return 0;
    }
  }

  write(sequence: number): void {
    try {
      globalThis.sessionStorage?.setItem(this.key, String(sequence));
    } catch {
      // Nothing to do: the cursor restarts at zero on the next load.
    }
  }
}

const cursorStorage = new SessionCursorStorage();

/**
 * Browsers freeze timers in a backgrounded tab and a laptop that sleeps takes
 * its socket with it. Neither reliably produces a close event, so the moments
 * the browser hands control back are used as explicit cues to verify the
 * connection instead of waiting for a heartbeat that will not fire.
 */
function watchForWake(reconnect: () => void): () => void {
  const wake = () => {
    if (document.visibilityState === 'visible') reconnect();
  };
  document.addEventListener('visibilitychange', wake);
  globalThis.addEventListener('online', wake);
  globalThis.addEventListener('focus', wake);
  return () => {
    document.removeEventListener('visibilitychange', wake);
    globalThis.removeEventListener('online', wake);
    globalThis.removeEventListener('focus', wake);
  };
}

export function RealtimeProvider({
  viewerUserId,
  children,
}: {
  viewerUserId: string;
  children: ReactNode;
}) {
  return (
    <CoreRealtimeProvider
      viewerUserId={viewerUserId}
      cursorStorage={cursorStorage}
      wakeSignals={watchForWake}
    >
      {children}
    </CoreRealtimeProvider>
  );
}
